import { createContext, useContext, useState, ReactNode } from "react";
import { Track } from "@/types";
import { useWhatsAppPolicyStatus } from "@/hooks/useWhatsAppPolicyStatus";
import { toast } from "sonner";

interface MusicContextType {
  currentTrack: Track | null;
  setCurrentTrack: (track: Track | null) => void;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  togglePlayPause: () => void;
  playTrack: (track: Track) => void;
  playAlbum: (tracks: Track[], startIndex?: number) => void;
  playNext: () => void;
  playPrevious: () => void;
  currentQueue: Track[];
  currentTrackIndex: number;
  isPlayerExpanded: boolean;
  setIsPlayerExpanded: (expanded: boolean) => void;
  minimizePlayer: () => void;
  isTransitioning: boolean;
  setIsTransitioning: (transitioning: boolean) => void;
  // Set by the full-bleed compact feed (49a/50a) while it's mounted, so the
  // docked mini-player bar hides itself rather than overlaying that
  // TikTok-style, chrome-free card — playback itself (and stream
  // tracking/payment logic, both housed in PlayerContainer) keeps running
  // unaffected, since this only ever toggles visibility.
  isImmersiveFeedActive: boolean;
  setIsImmersiveFeedActive: (active: boolean) => void;
}

const MusicContext = createContext<MusicContextType | undefined>(undefined);

export function MusicProvider({ children }: { children: ReactNode }) {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayerExpanded, setIsPlayerExpanded] = useState(false);
  const [currentQueue, setCurrentQueue] = useState<Track[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isImmersiveFeedActive, setIsImmersiveFeedActive] = useState(false);
  const { isBlocked } = useWhatsAppPolicyStatus();

  const togglePlayPause = () => {
    if (isBlocked) {
      toast.error("Please complete verification first");
      return;
    }
    setIsPlaying((prev) => !prev);
  };

  // Implement the playTrack method with transition state
  const playTrack = (track: Track) => {
    if (isBlocked) {
      toast.error("Please complete verification to play music");
      return;
    }
    // Set transitioning to prevent playback on old track
    setIsTransitioning(true);
    setIsPlaying(false); // Stop current playback first
    setCurrentTrack(track);
    setCurrentQueue([track]);
    setCurrentTrackIndex(0);
    // Don't set isPlaying here - PlayerContainer will set it when URL is ready
  };

  // Implement the playAlbum method with transition state
  const playAlbum = (tracks: Track[], startIndex: number = 0) => {
    if (isBlocked) {
      toast.error("Please complete verification to play music");
      return;
    }
    setIsTransitioning(true);
    setIsPlaying(false);
    setCurrentQueue(tracks);
    setCurrentTrackIndex(startIndex);
    setCurrentTrack(tracks[startIndex]);
    // Don't set isPlaying here - PlayerContainer will set it when URL is ready
  };

  // Implement navigation methods with transition state
  const playNext = () => {
    if (
      currentQueue.length > 0 &&
      currentTrackIndex < currentQueue.length - 1
    ) {
      setIsTransitioning(true);
      setIsPlaying(false);
      const nextIndex = currentTrackIndex + 1;
      setCurrentTrackIndex(nextIndex);
      setCurrentTrack(currentQueue[nextIndex]);
      // Don't set isPlaying here - PlayerContainer will set it when URL is ready
    }
  };

  const playPrevious = () => {
    if (currentQueue.length > 0 && currentTrackIndex > 0) {
      setIsTransitioning(true);
      setIsPlaying(false);
      const prevIndex = currentTrackIndex - 1;
      setCurrentTrackIndex(prevIndex);
      setCurrentTrack(currentQueue[prevIndex]);
      // Don't set isPlaying here - PlayerContainer will set it when URL is ready
    }
  };

  const minimizePlayer = () => {
    setIsPlayerExpanded(false);
  };

  return (
    <MusicContext.Provider
      value={{
        currentTrack,
        setCurrentTrack,
        isPlaying,
        setIsPlaying,
        togglePlayPause,
        playTrack,
        playAlbum,
        playNext,
        playPrevious,
        currentQueue,
        currentTrackIndex,
        isPlayerExpanded,
        setIsPlayerExpanded,
        minimizePlayer,
        isTransitioning,
        setIsTransitioning,
        isImmersiveFeedActive,
        setIsImmersiveFeedActive,
      }}
    >
      {children}
    </MusicContext.Provider>
  );
}

export function useMusic() {
  const context = useContext(MusicContext);
  if (context === undefined) {
    throw new Error("useMusic must be used within a MusicProvider");
  }
  return context;
}
